import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import './About.css';
import { aboutService, AboutSection, AboutValue } from '../admin/pages/about/aboutService';

// ===== AnimatedSection Component =====
interface AnimatedSectionProps {
  children: React.ReactNode;
  direction?: 'left' | 'right';
  delay?: number;
}

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  direction = 'left',
  delay = 0
}) => {
  const { ref, inView } = useInView({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`animated-section ${direction} ${inView ? 'in-view' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// ===== AnimatedTitle Component =====
interface AnimatedTitleProps {
  children: string;
  color?: 'mixed' | 'vision' | 'mission' | 'values';
}

const AnimatedTitle: React.FC<AnimatedTitleProps> = ({ children, color }) => {
  const { ref, inView } = useInView({ threshold: 0.3 });

  const renderTitle = () => {
    if (color === 'mixed' && children === 'About Nora') {
      return (
        <>
          <span style={{ color: '#1976d2' }}>About</span>{' '}
          <span style={{ color: '#2e7d32' }}>NORA</span>
        </>
      );
    }
    if (color === 'vision') {
      return (
        <>
          <span style={{ color: '#2e7d32' }}>Our</span>{' '}
          <span style={{ color: '#1976d2' }}>Vision</span>
        </>
      );
    }
    if (color === 'mission') {
      return (
        <>
          <span style={{ color: '#2e7d32' }}>Our</span>{' '}
          <span style={{ color: '#1976d2' }}>Mission</span>
        </>
      );
    }
    if (color === 'values') {
      return (
        <>
          <span style={{ color: '#2e7d32' }}>Core</span>{' '}
          <span style={{ color: '#1976d2' }}>Values</span>
        </>
      );
    }
    return children;
  };

  return (
    <h2
      ref={ref}
      className={`title-animation ${inView ? 'title-in-view' : ''}`}
    >
      {renderTitle()}
    </h2>
  );
};

// ===== ValuesList Component =====
const ValuesList: React.FC<{ values: AboutValue[] }> = ({ values }) => {
  const { ref, inView } = useInView({ threshold: 0.1 });

  return (
    <div 
      ref={ref} 
      className={`values-list ${inView ? 'values-in-view' : ''}`}
    >
      {values.map((value, index) => (
        <div key={value.id} className="value-item" style={{ animationDelay: `${index * 100}ms` }}>
          <div className="value-number">{String(index + 1).padStart(2, '0')}</div>
          <div className="value-content">
            <span className="value-english">{value.value_text}</span>
            {value.value_dari && (
              <span className="value-dari">({value.value_dari})</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ===== Main About Component =====
const About: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('about');
  const [sections, setSections] = useState<AboutSection[]>([]);
  const [values, setValues] = useState<AboutValue[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAboutContent();
  }, []);

  useEffect(() => {
    console.log('🔵 About component mounted, fetching data...');
    fetchAboutContent();
  }, []);

 const fetchAboutContent = async () => {
    try {
      console.log('🔵 Fetching about content...');
      const response = await aboutService.getPublicContent();
      console.log('🔵 API Response:', response);
      if (response.success) {
        console.log('🔵 Sections:', response.data.sections);
        console.log('🔵 Values:', response.data.values);
        setSections(response.data.sections);
        setValues(response.data.values);
      } else {
        console.error('🔴 API returned unsuccessful:', response);
      }
    } catch (error) {
      console.error('🔴 Failed to fetch about content:', error);
    } finally {
      setLoading(false);
      console.log('🔵 Loading set to false');
    }
  };

  const getTabContent = (tabKey: string): AboutSection | undefined => {
    return sections.find(s => s.tab_key === tabKey);
  };

  const getTabColor = (tabKey: string): 'mixed' | 'vision' | 'mission' | 'values' | undefined => {
    const map: Record<string, 'mixed' | 'vision' | 'mission' | 'values'> = {
      'about': 'mixed',
      'vision': 'vision',
      'mission': 'mission',
      'values': 'values'
    };
    return map[tabKey];
  };

  const handleReadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const section = getTabContent(activeTab);
    if (section) {
      navigate(section.button_link);
    }
  };

  if (loading) {
    console.log('🔵 Loading state: true');
    return (
      <div className="about-container">
        <div className="about-wrapper">
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="loading-spinner"></div>
            <p className="ml-3">Loading about content...</p>
          </div>
        </div>
      </div>
    );
  }
  console.log('🔵 Sections count:', sections.length);
  console.log('🔵 Active tab:', activeTab);

    if (sections.length === 0) {
    console.log('🔵 No sections found, showing fallback');
    return (
      <div className="about-container">
        <div className="about-wrapper">
          <div className="text-center py-12">
            <p className="text-gray-500">No about content available.</p>
          </div>
        </div>
      </div>
    );
  }

  // const activeSection = getTabContent(activeTab);
  const tabValues = activeTab === 'values' ? values : [];

  return (
    <div className="about-container">
      <div className="about-wrapper">

        {/* Tabs */}
        <div className="tabs-navigation">
          {sections.map((section) => (
            <button 
              key={section.id}
              className={`tab-button ${activeTab === section.tab_key ? 'active' : ''}`} 
              onClick={() => setActiveTab(section.tab_key)}
            >
              <span className="tab-text">{section.title}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="tab-content">
          {sections.map((section) => (
            <div 
              key={section.id}
              className={`tab-panel ${activeTab === section.tab_key ? 'active' : ''}`}
            >
              <div className="content-wrapper">
                <AnimatedSection direction="left">
                  <div className="image-section">
                    <img 
                      src={section.image_url} 
                      className="about-image" 
                      alt={section.title} 
                    />
                  </div>
                </AnimatedSection>

                <AnimatedSection direction="right">
                  <div className="text-section">
                    <AnimatedTitle color={getTabColor(section.tab_key)}>
                      {section.title}
                    </AnimatedTitle>
                    <p className="about-text">{section.content}</p>
                    
                    {section.tab_key === 'values' && tabValues.length > 0 && (
                      <ValuesList values={tabValues} />
                    )}
                    
                    <button className="btn-primary" onClick={handleReadMore}>
                      {section.button_text || 'Read More'}
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default About;







// frontend 
// import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useInView } from 'react-intersection-observer';
// import './About.css';
// import about from '../images/about_nora.jpg';
// import mission from '../images/mission_nora.jpg';
// import vision from '../images/vision_nora.jpeg';
// import value from '../images/new_12.jpg';

// // Type definitions
// type TabType = 'about' | 'vision' | 'mission' | 'values';

// interface TabContent {
//   title: string;
//   image: string;
//   content: string;
//   buttonText: string;
//   buttonLink: string;
//   values?: string[];
// }

// interface TabContentMap {
//   about: TabContent;
//   vision: TabContent;
//   mission: TabContent;
//   values: TabContent;
// }

// // Component for animating sections
// interface AnimatedSectionProps {
//   children: React.ReactNode;
//   direction?: 'left' | 'right';
//   delay?: number;
// }

// const AnimatedSection: React.FC<AnimatedSectionProps> = ({
//   children,
//   direction = 'left',
//   delay = 0
// }) => {
//   const { ref, inView } = useInView({ threshold: 0.1 });

//   return (
//     <div
//       ref={ref}
//       className={`animated-section ${direction} ${inView ? 'in-view' : ''}`}
//       style={{ animationDelay: `${delay}ms` }}
//     >
//       {children}
//     </div>
//   );
// };

// // Component for colored titles
// interface AnimatedTitleProps {
//   children: string;
//   color?: 'mixed' | 'vision' | 'mission' | 'values';
// }

// const AnimatedTitle: React.FC<AnimatedTitleProps> = ({ children, color }) => {
//   const { ref, inView } = useInView({ threshold: 0.3 });

//   const renderTitle = () => {
//     // About Nora → About (BLUE) + NORA (GREEN)
//     if (color === 'mixed' && children === 'About Nora') {
//       return (
//         <>
//           <span style={{ color: '#1976d2' }}>About</span>{' '}
//           <span style={{ color: '#2e7d32' }}>NORA</span>
//         </>
//       );
//     }

//     // Our Vision → Our (GREEN) + Vision (BLUE)
//     if (color === 'vision') {
//       return (
//         <>
//           <span style={{ color: '#2e7d32' }}>Our</span>{' '}
//           <span style={{ color: '#1976d2' }}>Vision</span>
//         </>
//       );
//     }

//     // Our Mission → Our (GREEN) + Mission (BLUE)
//     if (color === 'mission') {
//       return (
//         <>
//           <span style={{ color: '#2e7d32' }}>Our</span>{' '}
//           <span style={{ color: '#1976d2' }}>Mission</span>
//         </>
//       );
//     }

//     // Our Values → Our (GREEN) + Values (BLUE)
//     if (color === 'values') {
//       return (
//         <>
//           <span style={{ color: '#2e7d32' }}>Core</span>{' '}
//           <span style={{ color: '#1976d2' }}>Values</span>
//         </>
//       );
//     }

//     return children;
//   };

//   return (
//     <h2
//       ref={ref}
//       className={`title-animation ${inView ? 'title-in-view' : ''}`}
//     >
//       {renderTitle()}
//     </h2>
//   );
// };

// // Values List Component
// const ValuesList: React.FC<{ values: string[] }> = ({ values }) => {
//   const { ref, inView } = useInView({ threshold: 0.1 });

//   return (
//     <div 
//       ref={ref} 
//       className={`values-list ${inView ? 'values-in-view' : ''}`}
//     >
//       {values.map((value, index) => {
//         // Split the value into English and Dari parts
//         const match = value.match(/^(.*?)\s*\((.*?)\)$/);
//         const english = match ? match[1] : value;
//         const dari = match ? match[2] : '';
        
//         return (
//           <div key={index} className="value-item" style={{ animationDelay: `${index * 100}ms` }}>
//             <div className="value-number">{String(index + 1).padStart(2, '0')}</div>
//             <div className="value-content">
//               <span className="value-english">{english}</span>
//               {dari && <span className="value-dari">({dari})</span>}
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// const About: React.FC = () => {
//   const [activeTab, setActiveTab] = useState<TabType>('about');
//   const navigate = useNavigate();

//   const tabContent: TabContentMap = {
//     about: {
//       title: 'About Nora',
//       image: about,
//       content:
//         "Noor Organization for Rehabilitation of Afghanistan (NORA) is a national non-governmental organization established in 2025 by a dedicated team of university professors from Kabul Polytechnic University, Kabul Medical University, and Kabul Education University, together with experienced software engineers, Artificial Intelligence specialists, researchers, development professionals, and humanitarian practitioners who share a common vision for a healthier, educated, digitally empowered, and sustainable Afghanistan. Recognizing Afghanistan’s potential, resilience, and the transformative power of technology and innovation, NORA was established to improve the quality of life of Afghan communities through integrated programs in health, education, Artificial Intelligence (AI), digital skills, technology innovation, and community development. Afghanistan has experienced decades of challenges that have affected access to quality education, healthcare, employment opportunities, and modern technologies. However, Afghan communities continue to demonstrate creativity, resilience, and a strong desire for progress. NORA believes that sustainable development can be achieved by empowering people with knowledge, skills, innovation, and equal opportunities. NORA follows a community-centered and technology-driven approach, working closely with universities, government institutions, civil society organizations, development partners, and local communities to design practical, inclusive, and sustainable solutions that address current challenges and prepare Afghanistan for the future. As a technology-focused organization, NORA promotes Artificial Intelligence, Digital Transformation, Software Development, Digital Skills, and Innovation as key pillars for national development. Our team designs and develops digital platforms, management information systems, AI-powered applications, educational technologies, and customized technology solutions to support institutions, organizations, and communities. At the same time, NORA is committed to empowering Afghan youth, women, and children by providing access to digital education, professional skills development, remote employment opportunities, freelancing, entrepreneurship, and participation in the global digital economy. Through innovation, collaboration, and capacity building, NORA aims to strengthen human capital, modernize institutions through technology, expand digital inclusion, improve healthcare and education systems, and create sustainable economic opportunities for Afghan people. Working together with national and international organizations, universities, technology companies, government institutions, and local communities, NORA remains committed to transparency, accountability, innovation, and continuous learning to create measurable and lasting impact.",
//       buttonText: 'Read More',
//       buttonLink: '/vision'
//     },
//     vision: {
//       title: 'Our Vision',
//       image: vision,
//       content:
//         'To become Afghanistan’s most trusted and leading technology-driven organization, empowering youth and communities through innovation, digital skills, and advanced technologies to transform education, improve healthcare, and support sustainable economic development.',
//       buttonText: 'Learn More',
//       buttonLink: '/vision'
//     },
//     mission: {
//       title: 'Our Mission',
//       image: mission,
//       content:
//         'To empower Afghan youth and communities by leveraging technology and innovation to improve education and healthcare, develop digital skills, promote Artificial Intelligence, and create sustainable pathways for employment, entrepreneurship, and inclusive development.',
//       buttonText: 'Discover More',
//       buttonLink: '/vision'
//     },
//     values: {
//       title: 'Core Values',
//       image: value,
//       content: 'Our core values guide everything we do at NORA. These principles shape our approach to humanitarian work and community development.',
//       buttonText: 'Learn More',
//       buttonLink: '/vision',
//       values: [
//         'Integrity and accountability',
//         'Inclusion and dignity',
//         'Local ownership',
//         'Innovation with purpose',
//         'Evidence and learning',
//         'Do no harm'
//       ]
//     }
//   };

//   const handleReadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
//     e.preventDefault();
//     navigate(tabContent[activeTab].buttonLink);
//   };

//   return (
//     <div className="about-container">
//       <div className="about-wrapper">

//         {/* Tabs */}
//         <div className="tabs-navigation">
//           <button 
//             className={`tab-button ${activeTab === 'about' ? 'active' : ''}`} 
//             onClick={() => setActiveTab('about')}
//           >
//             <span className="tab-text">Who We Are</span>
//           </button>
//           <button 
//             className={`tab-button ${activeTab === 'vision' ? 'active' : ''}`} 
//             onClick={() => setActiveTab('vision')}
//           >
//             <span className="tab-text">Vision</span>
//           </button>
//           <button 
//             className={`tab-button ${activeTab === 'mission' ? 'active' : ''}`} 
//             onClick={() => setActiveTab('mission')}
//           >
//             <span className="tab-text">Mission</span>
//           </button>
//           <button 
//             className={`tab-button ${activeTab === 'values' ? 'active' : ''}`} 
//             onClick={() => setActiveTab('values')}
//           >
//             <span className="tab-text">Values</span>
//           </button>
//         </div>

//         {/* Content */}
//         <div className="tab-content">

//           {/* About */}
//           <div className={`tab-panel ${activeTab === 'about' ? 'active' : ''}`}>
//             <div className="content-wrapper">
//               <AnimatedSection direction="left">
//                 <div className="image-section">
//                   <img src={tabContent.about.image} className="about-image" alt="About NORA" />
//                 </div>
//               </AnimatedSection>

//               <AnimatedSection direction="right">
//                 <div className="text-section">
//                   <AnimatedTitle color="mixed">
//                     {tabContent.about.title}
//                   </AnimatedTitle>
//                   <p className="about-text">{tabContent.about.content}</p>
//                   <button className="btn-primary" onClick={handleReadMore}>
//                     {tabContent.about.buttonText}
//                     <i className="fas fa-arrow-right"></i>
//                   </button>
//                 </div>
//               </AnimatedSection>
//             </div>
//           </div>

//           {/* Vision */}
//           <div className={`tab-panel ${activeTab === 'vision' ? 'active' : ''}`}>
//             <div className="content-wrapper">
//               <AnimatedSection direction="left">
//                 <div className="image-section">
//                   <img src={tabContent.vision.image} className="about-image" alt="Our Vision" />
//                 </div>
//               </AnimatedSection>

//               <AnimatedSection direction="right">
//                 <div className="text-section">
//                   <AnimatedTitle color="vision">
//                     {tabContent.vision.title}
//                   </AnimatedTitle>
//                   <p className="about-text">{tabContent.vision.content}</p>
//                   <button className="btn-primary" onClick={handleReadMore}>
//                     {tabContent.vision.buttonText}
//                     <i className="fas fa-arrow-right"></i>
//                   </button>
//                 </div>
//               </AnimatedSection>
//             </div>
//           </div>

//           {/* Mission */}
//           <div className={`tab-panel ${activeTab === 'mission' ? 'active' : ''}`}>
//             <div className="content-wrapper">
//               <AnimatedSection direction="left">
//                 <div className="image-section">
//                   <img src={tabContent.mission.image} className="about-image" alt="Our Mission" />
//                 </div>
//               </AnimatedSection>

//               <AnimatedSection direction="right">
//                 <div className="text-section">
//                   <AnimatedTitle color="mission">
//                     {tabContent.mission.title}
//                   </AnimatedTitle>
//                   <p className="about-text">{tabContent.mission.content}</p>
//                   <button className="btn-primary" onClick={handleReadMore}>
//                     {tabContent.mission.buttonText}
//                     <i className="fas fa-arrow-right"></i>
//                   </button>
//                 </div>
//               </AnimatedSection>
//             </div>
//           </div>

//           {/* Values */}
//           <div className={`tab-panel ${activeTab === 'values' ? 'active' : ''}`}>
//             <div className="content-wrapper values-wrapper">
//               <AnimatedSection direction="left">
//                 <div className="image-section">
//                   <img src={tabContent.values.image} className="about-image" alt="Our Values" />
//                 </div>
//               </AnimatedSection>

//               <AnimatedSection direction="right">
//                 <div className="text-section values-text-section">
//                   <AnimatedTitle color="values">
//                     {tabContent.values.title}
//                   </AnimatedTitle>
//                   <p className="about-text">{tabContent.values.content}</p>
                  
//                   {/* Values List */}
//                   {tabContent.values.values && (
//                     <ValuesList values={tabContent.values.values} />
//                   )}
                  
//                   <button className="btn-primary" onClick={handleReadMore}>
//                     {tabContent.values.buttonText}
//                     <i className="fas fa-arrow-right"></i>
//                   </button>
//                 </div>
//               </AnimatedSection>
//             </div>
//           </div>

//         </div>
//       </div>
//     </div>
//   );
// };

// export default About;




